import React, { createContext, useContext, useState, useEffect, useCallback, useMemo } from 'react';
import { useMsal } from '@azure/msal-react';
import { getApiClient } from './apiClient';
import { useProfileApiOptional } from '../contexts/ProfileApiContext';
import logger from './logger';

export const UserTypeContext = createContext();

export const USER_TYPES = {
  PARTNER: 'partner',
  CUSTOMER: 'customer',
  INVESTORS_REGULATORS: 'investors-regulators'
};

export const VIEW_MODES = {
  TECHNICAL: 'technical',
  NORMAL: 'normal'
};

export const useUserType = () => {
  const context = useContext(UserTypeContext);
  if (!context) {
    throw new Error('useUserType must be used within a UserTypeProvider');
  }
  return context;
};

export const UserTypeProvider = ({ children }) => {
  const { accounts } = useMsal();
  const profileApi = useProfileApiOptional();
  const [userType, setUserType] = useState(USER_TYPES.CUSTOMER); // Default to customer
  const [viewMode, setViewMode] = useState(VIEW_MODES.NORMAL); // Default to normal for authenticated users
  const [userDocumentType, setUserDocumentType] = useState(null); // 'end_user' or 'full_user' from user document
  const [isLoading, setIsLoading] = useState(true);

  // Load user type and view mode from localStorage on mount
  useEffect(() => {
    const savedUserType = localStorage.getItem('userType');
    const savedViewMode = localStorage.getItem('viewMode');
    
    if (savedUserType && Object.values(USER_TYPES).includes(savedUserType)) {
      setUserType(savedUserType);
    }
    
    if (savedViewMode && Object.values(VIEW_MODES).includes(savedViewMode)) {
      setViewMode(savedViewMode);
    }
    
    setIsLoading(false);
  }, []);

  // Prefer ProfileApiContext when available; otherwise fetch user profile for user_type
  useEffect(() => {
    const applyUserData = (userData) => {
      if (!userData) return;
      let documentUserType = userData.user_type || userData.userType || 'full_user';
      const onboardingUserType = userData.onboarding_user_type;
      if (documentUserType === 'end_user' && onboardingUserType === 'customer') {
        logger.warn('USER-TYPE-CONTEXT', 'User has end_user but chose customer during onboarding - treating as full_user');
        documentUserType = 'full_user';
        try {
          getApiClient().updateUserProfile({ user_type: 'full_user' }).catch(() => {});
        } catch (e) {
          logger.warn('USER-TYPE-CONTEXT', 'Could not fix user_type', { error: e.message });
        }
      }
      setUserDocumentType(documentUserType);
      if (documentUserType === 'end_user') {
        setViewMode(VIEW_MODES.NORMAL);
        localStorage.setItem('viewMode', VIEW_MODES.NORMAL);
      }
      if (onboardingUserType === 'reseller') {
        setViewMode(VIEW_MODES.TECHNICAL);
        localStorage.setItem('viewMode', VIEW_MODES.TECHNICAL);
      }
      const settings = userData.settings || {};
      if (settings.has_seen_technical_guide === true) {
        localStorage.setItem('b-esper_technicalguide', 'true');
      }
    };

    if (!accounts || accounts.length === 0) {
      setUserDocumentType(null);
      return;
    }
    if (profileApi?.profile) {
      applyUserData(profileApi.profile);
      return;
    }
    const fetchUserProfile = async () => {
      try {
        const apiClient = getApiClient();
        const profileResponse = await apiClient.getUserProfile();
        if (profileResponse?.success && profileResponse.data) {
          applyUserData(profileResponse.data);
        }
      } catch (error) {
        logger.warn('USER-TYPE-CONTEXT', 'Failed to fetch user profile for user_type', { error: error.message });
        setUserDocumentType('full_user');
      }
    };
    fetchUserProfile();
  }, [accounts, profileApi?.profile]);

  // Save user type to localStorage when it changes
  const updateUserType = useCallback((newUserType) => {
    if (Object.values(USER_TYPES).includes(newUserType)) {
      setUserType(newUserType);
      localStorage.setItem('userType', newUserType);
    }
  }, []);

  // Save view mode to localStorage when it changes
  // Prevent changes for end users
  const updateViewMode = useCallback((newViewMode) => {
    if (userDocumentType === 'end_user') {
      setViewMode(VIEW_MODES.NORMAL);
      localStorage.setItem('viewMode', VIEW_MODES.NORMAL);
      return;
    }
    if (Object.values(VIEW_MODES).includes(newViewMode)) {
      setViewMode(newViewMode);
      localStorage.setItem('viewMode', newViewMode);
    }
  }, [userDocumentType]);

  const value = useMemo(() => ({
    userType,
    setUserType: updateUserType,
    viewMode,
    setViewMode: updateViewMode,
    userDocumentType,
    isEndUser: userDocumentType === 'end_user',
    isFullUser: userDocumentType === 'full_user' || userDocumentType === null,
    isLoading,
    isPartner: userType === USER_TYPES.PARTNER,
    isCustomer: userType === USER_TYPES.CUSTOMER,
    isInvestorsRegulators: userType === USER_TYPES.INVESTORS_REGULATORS,
    isTechnical: viewMode === VIEW_MODES.TECHNICAL,
    isNormal: viewMode === VIEW_MODES.NORMAL
  }), [userType, viewMode, userDocumentType, isLoading, updateUserType, updateViewMode]);

  return (
    <UserTypeContext.Provider value={value}>
      {children}
    </UserTypeContext.Provider>
  );
};
